import React, { useEffect, useState, useRef } from "react";
import { Box } from "@mui/material";
import { modifyOrderQuote } from "../../http/api";
import useSnackbar from "../../Utils/useSnackbar";
import {
  RFQ_TABLE_MARGIN_TOP,
  RFQ_TABLE_MARGIN_BOTTOM,
  INITIAL_ANIMATION_DELAY_MS,
  TRANSLATION_Y_OFFSET,
  BRANCH_WIDTH,
  BRANCH_WIDTH_PX,
  ENHANCED_RFQ_PROTOCOLS,
  PROTOCOL_COLUMN_WIDTH
} from "./RfqComponents/RfqConstants";
import RfqBranchVisualization from "./RfqComponents/RfqBranchVisualization";
import RfqTable from "./RfqComponents/RfqTable";
import { parseVenues, isButtonEnabled, isOrderExpired } from "./RfqComponents/RfqUtils";
import { useWebSocketQuoteUpdates } from "./RfqComponents/useWebSocketQuoteUpdates";
import CounterPriceModal from "./RfqComponents/CounterPriceModal";
import { ORDER_STATUS, ACTION_CODE } from "../../Constants/AppConstants";
import { tokens, withAlpha } from '../../Common/designTokens'; // added

const computeProtocolMetrics = (cols, widths) => {
  if (!Array.isArray(cols)) return null;
  const protocolIndex = cols.findIndex((col) => col.key === "protocol");
  if (protocolIndex < 0) return null;
  const protocolWidth = widths?.protocol ?? PROTOCOL_COLUMN_WIDTH;
  const leftBeforeProtocol = cols
    .slice(0, protocolIndex)
    .reduce((acc, col) => acc + (widths?.[col.key] ?? 150), 0);
  return { leftBeforeProtocol, protocolWidth };
};

const RfqBranchTable = ({
  parentRowData,
  parentCellRef,
  columnWidths,
  firstTwoColumnKeys,
  colsBondTable,
  orderData,
  setOrderData,
}) => {
  const branchMidpoint = `${BRANCH_WIDTH_PX / 2}px`;
  const resolveInitialTableLeft = () => {
    const metrics = computeProtocolMetrics(colsBondTable, columnWidths);
    return metrics ? metrics.leftBeforeProtocol : 0;
  };

  const [animationState, setAnimationState] = useState("entering");
  const [linePosition, setLinePosition] = useState({
    left: branchMidpoint,
    connector: branchMidpoint,
  });
  const [tablePosition, setTablePosition] = useState(() => ({
    left: resolveInitialTableLeft(),
  }));
  const [connectorWidth, setConnectorWidth] = useState(`${BRANCH_WIDTH_PX}px`);
  const tableContainer = useRef(null);
  const [counterModalOpen, setCounterModalOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [venues, setVenues] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAcceptedQuote, setHasAcceptedQuote] = useState(false);
  const [allQuotesRejected, setAllQuotesRejected] = useState(false);
  const { showSuccess, showError, SnackbarComponent } = useSnackbar();
  const expiryCheckPerformedRef = useRef(false);

  useWebSocketQuoteUpdates(setOrderData);

  useEffect(() => {
    if (parentCellRef && parentCellRef.current && colsBondTable) {
      const updatePosition = () => {
        try {
          const metrics = computeProtocolMetrics(colsBondTable, columnWidths);
          if (!metrics) return;

          const tableLeft = metrics.leftBeforeProtocol;
          setTablePosition({ left: tableLeft });

          const protocolLeft = metrics.leftBeforeProtocol;
          const protocolCenter = protocolLeft + metrics.protocolWidth / 2;
          const lineLeftRaw = protocolCenter - tableLeft;
          const boundedLineLeft = Math.max(0, Math.min(lineLeftRaw, BRANCH_WIDTH_PX));

          setLinePosition({
            left: `${boundedLineLeft}px`,
            connector: `${boundedLineLeft}px`,
          });

          const connectorDistance = BRANCH_WIDTH_PX - boundedLineLeft;
          setConnectorWidth(`${Math.max(connectorDistance, 0)}px`);
        } catch (error) {
          console.error("Error calculating positions:", error);
        }
      };

      updatePosition();
      window.addEventListener("resize", updatePosition);
      return () => window.removeEventListener("resize", updatePosition);
    }
  }, [parentCellRef, columnWidths, firstTwoColumnKeys, colsBondTable]);

  // Animation effect to transition from "entering" to "entered"
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationState("entered");
    }, INITIAL_ANIMATION_DELAY_MS);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    expiryCheckPerformedRef.current = false;
    return () => {
      expiryCheckPerformedRef.current = false;
    };
  }, [parentRowData?.orderId]);

  // Find the full order data from the central state by orderId
  const getFullOrderData = () => {
    if (!orderData || !parentRowData?.orderId) return null;
    
    // Always use orderId for data operations (backend communication)
    return orderData.find((order) => order.orderId === parentRowData.orderId);
  };

  // Get original quote data (not formatted with "--") directly from orderData
  const getOriginalQuote = (orderQuotesId) => {
    const fullOrder = getFullOrderData();
    if (!fullOrder || !fullOrder.orderQuotes) return null;
    
    const foundQuote = fullOrder.orderQuotes.find(
      (quote) => quote.orderQuotesId === orderQuotesId
    );

    // If found, merge the parent full order's protocol into the quote object
    // so API calls receive protocol along with the quote.
    if (foundQuote) {
      return {
        ...foundQuote,
        protocol: fullOrder.protocol,
      };
    }

    return null;
  };

  // Refs to avoid redundant updates and re-run loops
  const prevVenuesSigRef = useRef(null);
  const prevQuotesSigRef = useRef(null);

  // Initialize venues from the full order data and update when orderData changes
  useEffect(() => {
    const fullOrderData = getFullOrderData();
    if (!fullOrderData) return;

    // Build parsed venues and compute a light signature
    const parsedVenues = parseVenues(fullOrderData) || [];
    const newVenuesSig = parsedVenues
      .map(v => `${v.orderQuotesId}:${v.status ?? ""}:${v.counterPrice ?? ""}:${v.price ?? ""}`) // include price to catch price updates
      .join("|");

    // Only update venues state if signature actually changed
    if (newVenuesSig !== prevVenuesSigRef.current) {
      prevVenuesSigRef.current = newVenuesSig;
      setVenues(parsedVenues);
      // Derive flags only when venues change
      const hasAccepted = parsedVenues.some(v => {
        const st = (v.status || "").toUpperCase();
        return st === "ACCEPTED" || st === "ACCEPTANCE_ACKED"; // treat ACK same as accepted
      });
      const allRejected = parsedVenues.length > 0 && parsedVenues.every(venue => venue.status === "REJECTED");
      setHasAcceptedQuote(hasAccepted);
      setAllQuotesRejected(allRejected && !hasAccepted);
    }

  }, [orderData, parentRowData?.orderId]); 

  // Check for expired order when RFQ is expanded, but only once
  useEffect(() => {
    const checkForExpiredOrder = async () => {
      if (expiryCheckPerformedRef.current) {
        return;
      }

      const fullOrderData = getFullOrderData();
      if (!fullOrderData || !fullOrderData.orderQuotes) return;
      
      const token = localStorage.getItem("token");
      if (!token) return;
      
      // Check if the order itself is expired
      if (isOrderExpired(fullOrderData)) {
        // If the order is expired, mark all quotes as expired
        if (fullOrderData.orderQuotes && fullOrderData.orderQuotes.length > 0) {
          // Take the first quote for the API call (per requirement, any quote will work)
          const firstQuote = fullOrderData.orderQuotes[0];
          
          try {
            await modifyOrderQuote(token, firstQuote, 4); // action 4 = Expire
          } catch (error) {
            console.error("Failed to update expired order quotes:", error);
          }
        }
      }
      
      expiryCheckPerformedRef.current = true;
    };
    
    if (animationState === "entered") {
      checkForExpiredOrder();
    }
  }, [animationState]);

  // Update the global order data state
  const updateOrderQuotes = (updatedVenue, newStatus) => {
    if (!setOrderData || !parentRowData?.orderId) return;

    // Commented out manual status update since WebSocket handles this now
    /*
    setOrderData((prevOrders) =>
      prevOrders.map((order) => {
        if (order.orderId !== parentRowData.orderId) return order;

        return {
          ...order,
          orderQuotes: order.orderQuotes.map((quote) =>
            quote.orderQuotesId === updatedVenue.orderQuotesId
              ? {
                  ...quote,
                  counterPrice: updatedVenue.counterPrice,
                  status: newStatus,
                }
              : quote
          ),
        };
      })
    );
    */
  };

  // Update local state after accept action
  const updateLocalStateAfterAction = (venueId, newStatus) => {
    // Commented out manual status update since WebSocket handles this now
    /*
    setVenues((prevVenues) => {
      const updatedVenues = prevVenues.map((v) =>
        v.orderQuotesId === venueId ? { ...v, status: newStatus } : v
      );
      
      // Check if the new status is "ACCEPTED"
      if (newStatus === "ACCEPTED") {
        setHasAcceptedQuote(true);
      }
      
      return updatedVenues;
    });
    */
  };

  // Handle button actions
  const handleAction = async (action, venue) => {
  

    if (action === "counter") {
      setSelectedVenue(venue);
      setCounterModalOpen(true);
      return;
    }

    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      let actionCode;
      let newStatus;

      // Map action string to API action code
      switch (action) {
        case "accept":
          actionCode = ACTION_CODE.ACCEPT;
          break;
        case "reject":
          actionCode = ACTION_CODE.REJECT;
          break;
        case "counter_cancel":
          actionCode = ACTION_CODE.COUNTER_CANCEL;
          break;
        default:
          throw new Error("Invalid action");
      }

      const originalQuote = getOriginalQuote(venue.orderQuotesId);
      if (!originalQuote) {
        throw new Error("Quote not found in order data");
      }
      
      if (originalQuote.status?.toLowerCase() === ORDER_STATUS.EXPIRED) {
        showError("This quote has expired and cannot be modified");
        return;
      }

      // Call API with the entire venue object and action code
      const response = await modifyOrderQuote(token, originalQuote, actionCode);
      
      // Handle backend returning success: false (200 OK but operation failed)
      if (response && response.success === false) {
        showError(response.message || `Failed to ${action} quote`);
        return;
      }
      
      // Check response for status to handle possible expiry
      if (response && response.status?.toLowerCase() === ORDER_STATUS.EXPIRED) {
        showError("This quote has expired and cannot be modified");
        return;
      }
      
      if(action === "counter_cancel"){
        showSuccess("Counter cancelled successfully");
      } else {
        showSuccess(`Quote ${action === "accept" ? "accepted" : "rejected"} successfully`);
      }
    } catch (error) {
      console.error(`Error ${action}ing quote:`, error);
      
      // User-friendly error messages based on error type
      if (error.response) {
        // Server returned an error response
        const status = error.response.status;
        
        if (status === 400) {
          // Check for expired quote error message pattern
          if (error.response.data && typeof error.response.data === 'string' && 
              (error.response.data.includes('expired') || error.response.data.includes('Expired'))) {
            showError("This quote has expired and cannot be modified");
            // Commented out manual status update since WebSocket handles this now
            // updateLocalStateAfterAction(venue.orderQuotesId, "EXPIRED");
          } else {
            showError("The server could not process your request");
          }
        } else if (status === 401 || status === 403) {
          showError("You don't have permission to perform this action");
        } else if (status === 404) {
          showError("The quote could not be found");
        } else if (status === 409) {
          showError("The quote has been modified by someone else");
        } else if (status >= 500) {
          showError("The server encountered an error. Please try again later");
        } else {
          showError(`Failed to ${action} quote`);
        }
      } else if (error.request) {
        // Request made but no response received
        showError("Could not connect to the server. Please check your internet connection");
      } else {
        // Something else happened
        showError(error.message || `Failed to ${action} quote`);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle counter price submission
  const handleCounterSubmit = async (updatedVenue) => {

    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");

      const originalQuote = getOriginalQuote(updatedVenue.orderQuotesId);
      if (!originalQuote) {
        throw new Error("Quote not found in order data");
      }
      
      if (originalQuote.status?.toLowerCase() === ORDER_STATUS.EXPIRED) {
        showError("This quote has expired and cannot be countered");
        return;
      }

      const quoteToSubmit = {
        ...originalQuote,
        counterPrice: updatedVenue.counterPrice,
      };

      // Call API with the entire quote object and action code 1 (Counter)
      const response = await modifyOrderQuote(
        token,
        quoteToSubmit,
        ACTION_CODE.COUNTER // action 1 = Counter
      );
      
      // Handle backend returning success: false (200 OK but operation failed)
      if (response && response.success === false) {
        showError(response.message || "Failed to submit counter price");
        return;
      }
      
      if (response && response.status?.toLowerCase() === ORDER_STATUS.EXPIRED) {
        showError("This quote has expired and cannot be countered");
        return;
      }

      showSuccess("Counter price submitted successfully");
    } catch (error) {
      console.error("Error submitting counter price:", error);
      
      // User-friendly error messages based on error type
      if (error.response) {
        const status = error.response.status;
        
        if (status === 400) {
          // Check for expired quote error pattern
          if (error.response.data && typeof error.response.data === 'string' && 
              (error.response.data.includes('expired') || error.response.data.includes('Expired'))) {
            showError("This quote has expired and cannot be countered");
            // Commented out manual status update since WebSocket handles this now
            /*
            // Update venue status to expired
            setVenues(prevVenues => 
              prevVenues.map(venue => 
                venue.orderQuotesId === updatedVenue.orderQuotesId 
                  ? { ...venue, status: "EXPIRED" } 
                  : venue
              )
            );
            */
          } else {
            showError("Invalid counter price. Please check your entry");
          }
        } else if (status === 401 || status === 403) {
          showError("You don't have permission to counter this quote");
        } else if (status === 404) {
          showError("The quote could not be found");
        } else if (status === 409) {
          showError("The quote has been modified by someone else");
        } else if (status >= 500) {
          showError("The server encountered an error. Please try again later");
        } else {
          showError("Failed to submit counter price");
        }
      } else if (error.request) {
        showError("Could not connect to the server. Please check your internet connection");
      } else {
        showError(error.message || "Failed to submit counter price");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Determine if this RFQ should use enhanced (Trumid) rules
  const isTrumidRfq = React.useMemo(() => {
    const proto = parentRowData?.protocol ? String(parentRowData.protocol) : "";
    return ENHANCED_RFQ_PROTOCOLS.some(p => p.toLowerCase() === proto.toLowerCase());
  }, [parentRowData?.protocol]);

  // current user id (consistent with other components)
  const currentUserId = localStorage.getItem("userId");

  return (
    <>
      <Box
        ref={tableContainer}
        sx={{
          display: "flex",
          flexDirection: "row",
          mt: RFQ_TABLE_MARGIN_TOP,
          mb: RFQ_TABLE_MARGIN_BOTTOM,
          ml: `${tablePosition.left}px`, // Dynamic margin left based on first column width
          opacity: animationState === "entered" ? 1 : 0,
          transform:
            animationState === "entered"
              ? "translateY(0)"
              : `translateY(${TRANSLATION_Y_OFFSET})`,
          transition: `opacity 0.3s ease-in-out, transform 0.3s ease-in-out`,
          overflow: "hidden",
          willChange: "opacity, transform",

        }}
      >
        {/* Branch Visualization Component */}
        <RfqBranchVisualization
          venues={venues}
          animationState={animationState}
          linePosition={linePosition}
          connectorWidth={connectorWidth}
        />

        {/* Table Component */}
        <RfqTable
          venues={venues}
          animationState={animationState}
          isLoading={isLoading}
          isButtonEnabled={isButtonEnabled}
          onAction={handleAction}
          hasAcceptedQuote={hasAcceptedQuote}
          allQuotesRejected={allQuotesRejected}
          isTrumidRfq={isTrumidRfq}
          rfqStatus={parentRowData?.rfqStatus}
          rfqInquiryType={parentRowData?.inquiryType || parentRowData?.rfqInquiryType}
          protocolName={parentRowData?.protocol}
          // NEW props: entryBy and currentUserId to control action column access
          entryBy={parentRowData?.entryBy}
          currentUserId={currentUserId}
        />
      </Box>

      {/* Counter Price Modal */}
      <CounterPriceModal
        open={counterModalOpen}
        onClose={() => setCounterModalOpen(false)}
        venueData={selectedVenue}
        onSubmit={handleCounterSubmit}
      />

      <SnackbarComponent />
    </>
  );
};

export default RfqBranchTable;